import { decode } from '@/utils/encrypt.util';

/**
 * Parse the tenant segment out of an app URL path (e.g. `/:base64UserId/assets`).
 *
 * The tenant is a base64-encoded email in the first path segment. Returns the
 * decoded tenant email, or `''` when there is no tenant segment or the segment
 * is malformed (invalid base64 decodes to the `''` fallback).
 *
 * This is the SINGLE tenant-path parser in the codebase. Consumers:
 * - `getCurrentTenantId()` (tenant-scoped storage keys) — call-time, per-tab
 * - `auth.tsx` `tenantFromUrl()` / mount whoami read / OAuth deep-link
 *
 * Because it reads the tab's URL path synchronously, tenant scoping is
 * per-tab and self-updating on navigation (no cross-tab global-slot bleed).
 *
 * @param pathname - The URL path (typically `window.location.pathname`)
 * @returns The decoded tenant email string, or `''` if absent/malformed
 */
export function tenantFromPath(pathname: string): string {
  const parts = pathname.split(/[?#]/)[0].split('/');
  const segment = parts.length > 2 ? parts[1] : '';
  if (!segment) return '';
  // decode() JSON.parses under safeExecute with an unchecked `as T` cast, so
  // despite the declared string return it can yield a non-string at runtime
  // (e.g. 'MTIz' → atob → '123' → JSON.parse → number 123). Guard the type so
  // the function's string contract holds.
  const decoded = decode<string>(decodeURIComponent(segment), '');
  return typeof decoded === 'string' ? decoded : '';
}
